/**
 * LoginProviderButtons — the Google/Apple provider sign-in column of the login
 * screen.
 *
 * This component owns only the column layout + the login screen's parity
 * ledger; the two buttons themselves come from the shared
 * `ProviderSignInButtons` (`components/common/auth`), which the Delete Account
 * page renders too — so the pair cannot drift between the two surfaces.
 *
 * Flutter source of truth: `LoginScreen.loginButtons()`
 * (`lib/presentation/screens/login/login_screen.dart:337-356`) plus
 * `googleLoginButton()` (`:358-398`) and `appleLoginButton()` (`:400-442`):
 *   - A `Column(crossAxisAlignment: stretch)` (`:344-345`) — both buttons are
 *     full-bleed within the column, which the web mirrors with `w-full` on the
 *     shared buttons.
 *   - Each button carries its 20x20 brand mark from `assets/images/<provider>.png`
 *     (`:378-382` / `:421-425`) followed by the label `'Continue with Google'`
 *     (`:388`) / `'Continue with Apple'` (`:431`) at `signInButtonTextStyle` —
 *     see `ProviderSignInButtons` for how the web renders that pair.
 *   - Taps go to `_login(context, false)` (`:363`) / `_login(context, true)`
 *     (`:404`) — the web's `onLoginWithGoogle` / `onLoginWithApple`, both wired
 *     by `LoginPage` to the shared `useProviderLogin` flow.
 *
 * Intentional web deviations (both pre-existing on `main`, kept deliberately):
 *
 *   1. **Both providers are always shown.** Flutter switches on the platform
 *      (`:347-351`): iOS shows Apple — plus Google when
 *      `RemoteConfigService.useGoogleLoginOnApple` — and every other platform
 *      shows Google only. Neither half of that switch has a web meaning:
 *      `Platform.isIOS` does not exist in the browser, and that remote-config
 *      key has no web getter by design (see the remote-config divergence note in
 *      `expause_web/docs/INDEX.md`). The web therefore offers both providers
 *      unconditionally, Google first.
 *
 *      Flutter goes further on web than that button switch alone suggests:
 *      `_login` short-circuits on `if (kIsWeb)` straight to
 *      `AppAuthenticationService.signInWithGoogle()` (`:96-105`), ignoring its
 *      own `isApple` argument — so Flutter-on-web routes *every* provider tap
 *      to Google and has no working Apple path at all. That branch is
 *      vestigial rather than a live web contract: the same file imports
 *      `dart:io` (`:1`) for the `Platform.isIOS` switch above, which a Flutter
 *      web build cannot compile, so the screen never actually runs on web —
 *      this React app is the web target. The port therefore deliberately does
 *      NOT mirror the `kIsWeb` short-circuit: its Apple button runs a real
 *      `signInWithAppleUseCase` (`useProviderLogin.ts`, the step-1 ternary in
 *      `loginWithProvider`). Recorded here so a later parity pass does not
 *      "fix" the Apple button into a second Google button.
 *
 *   2. **Fixed-width cap instead of 3/4 of the viewport.** Flutter constrains
 *      the column to `3/4 * MediaQuery.size.width` (`:340-343`), which on a
 *      desktop browser would sprawl the buttons across most of the window. The
 *      web caps at `responsive.auth.buttonsMaxWidth` (384px) instead — the
 *      desktop-viewport adaptation that `DeleteAccountLoginSection` already
 *      mirrors, now off the same token so the two surfaces cannot drift.
 *
 * The inter-button gap is `responsive.gap.lg` rather than Flutter's
 * `Padding(bottom: 10)` (`:407`) — a styling idiom, matching the spacing this
 * column has always rendered on the web.
 *
 * This component paints NO outer margin — the section spacing around the button
 * column is owned by `LoginPage`.
 */
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ProviderSignInButtons } from '@/presentation/components/common/auth/ProviderSignInButtons';

interface LoginProviderButtonsProps {
  /** Disables both buttons while a sign-in flow is in progress. */
  isLoggingIn: boolean;
  onLoginWithGoogle: () => void;
  onLoginWithApple: () => void;
}

export const LoginProviderButtons = ({
  isLoggingIn,
  onLoginWithGoogle,
  onLoginWithApple,
}: LoginProviderButtonsProps) => {
  const responsive = useResponsiveDesign();

  return (
    <div
      className="w-full"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: responsive.gap.lg,
        maxWidth: responsive.auth.buttonsMaxWidth,
      }}
    >
      <ProviderSignInButtons
        isLoggingIn={isLoggingIn}
        onLoginWithGoogle={onLoginWithGoogle}
        onLoginWithApple={onLoginWithApple}
        qa={{ googleId: 'login-google', appleId: 'login-apple' }}
      />
    </div>
  );
};
